import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Typography, Menu, MenuItem, Collapse, SxProps, Box } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useState, useEffect } from 'react';
import { HeaderNavigationLinksItem, NavigationLink as NavigationLinkItem } from '@/types/generated';
import { itemIsNavigationGroup, itemIsNavigationLink } from './NavigationBarItem.helpers';
import { navItemWithIconStyles, dropMenuStyles, navGroupLinkStyles, underlineStyles, getNavItemStyles, mobileNavItemStyles, mobileNavLinkTypographyResetStyles, navLinkHeight, collapseMenuItemStyles, mobileNavTopItemStyles } from './NavigationBarItem.styles';
import { getSubNavLinkStyles } from '@components/Header/GroupNavList/GroupNavItem.styles';
import NavigationLink from '@shared-components/components/NavigationLink/NavigationLink';
import TAGLink from '@shared-components/components/TAGLink';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import { incorrectUsageWarning } from '@utils/miscUtils';
type GroupNavTitleProps = {
  linkText: string;
  isMobile?: boolean;
  isActive: boolean;
};
function GroupNavTitle({
  linkText,
  isMobile,
  isActive
}: Readonly<GroupNavTitleProps>) {
  return <Typography variant="bodyLargeSemiBold" color={isMobile ? 'text.interactive' : 'text.primary'} {...!isMobile && {
    sx: [navGroupLinkStyles.groupLinkTitleStyles, navItemWithIconStyles]
  }} data-sentry-element="Typography" data-sentry-component="GroupNavTitle" data-sentry-source-file="NavigationBarItem.tsx">
			{linkText}
			{isMobile ? <Typography component="span" color="tertiary.main" sx={{
      lineHeight: 'normal',
      ...navGroupLinkStyles.getMobileGroupLinkIconStyles(isActive)
    }}>
					{/* Drill-down drawer item, so a forward chevron (not a down arrow) that rotates to "down" once open */}
					<TAGSvgIcon fontSize="small" icon="ChevronRightFunc" size={24} />
				</Typography> : <Typography color="tertiary.main" sx={{
      display: 'inline'
    }}>
					<KeyboardArrowDownIcon sx={navGroupLinkStyles.getGroupLinkIconStyles(isActive)} />
				</Typography>}
		</Typography>;
}
type GroupNavigationLinkProps = {
  navItem: NavigationLinkItem;
  index: number;
  isMobile?: boolean;
};
function GroupNavigationLink({
  navItem,
  index,
  isMobile
}: Readonly<GroupNavigationLinkProps>) {
  if (isMobile) {
    return <NavigationLink key={navItem.sys.id} {...navItem} isInternal={Boolean((navItem as {
      isInternal?: boolean;
    }).isInternal)} itemIndex={index} dataTestId={`link_navlink_nav_item_${navItem.sys.id}`}
    // Matches GroupNavList's mobile drill-down sub-link appearance
    textVariant="bodyMediumBook" navLinkSx={getSubNavLinkStyles()} />;
  }
  return <MenuItem key={navItem.sys.id} data-sentry-element="MenuItem" data-sentry-component="GroupNavigationLink" data-sentry-source-file="NavigationBarItem.tsx">
			<NavigationLink {...navItem} isInternal={Boolean((navItem as {
      isInternal?: boolean;
    }).isInternal)} itemIndex={index} dataTestId={`link_navlink_nav_item_${navItem.sys.id}`} disableUnderline data-sentry-element="NavigationLink" data-sentry-source-file="NavigationBarItem.tsx" />
		</MenuItem>;
}
interface INavigationBarItem {
  item: HeaderNavigationLinksItem;
  itemIndex: number;
  isMobile?: boolean;
  isUtilityNavigation?: boolean;
  navLinkStyles?: SxProps;
  linkColor?: string;
  fontWeight?: string | number;
  showIconText?: boolean;
  iconSize?: number;
}
export default function NavigationBarItem({
  item,
  itemIndex,
  isMobile,
  isUtilityNavigation = false,
  navLinkStyles = {},
  linkColor,
  fontWeight,
  showIconText = false,
  iconSize
}: Readonly<INavigationBarItem>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeLinkId, setActiveLinkId] = useState<string | null>(null);
  const open = Boolean(anchorEl);
  const router = useRouter();
  const closeMenu = () => {
    setAnchorEl(null);
    setActiveLinkId(null);
  };
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    event.preventDefault();
    if (id === activeLinkId) {
      closeMenu();
    } else {
      setAnchorEl(event.currentTarget);
      setActiveLinkId(id);
    }
  };
  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    closeMenu();
  };
  useEffect(() => {
    const handleScroll = () => {
      closeMenu();
    };
    globalThis.window.addEventListener('scroll', handleScroll);
    return () => {
      globalThis.window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  useEffect(() => {
    const handleRouteChange = () => {
      closeMenu();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);
  const navMobileStyles = isUtilityNavigation ? mobileNavItemStyles : mobileNavTopItemStyles;
  if (itemIsNavigationGroup(item)) {
    const groupMenuId = `header_navigation_group_menu_${itemIndex}`;

    // The mobile MenuList lays items out with a flex `gap`; keeping the link and its (possibly hidden) Collapse
    // as one wrapper here means that gap only applies once per group, same as a plain link.
    return <Box>
				{item.groupLink?.linkText && <TAGLink dataTestId={`link_page_nav_group_item_${item.sys.id}`} onClick={e => handleClick(e, item.sys.id)} aria-controls={groupMenuId} aria-haspopup="true" analyticsContext={item.groupLink?.analyticsContext} linkSx={{
        ...(isMobile ? navMobileStyles : underlineStyles),
        ...navLinkHeight,
        ...navLinkStyles
      }} className={activeLinkId === item.sys.id ? 'active' : ''}>
						<GroupNavTitle linkText={item.groupLink.linkText} isMobile={isMobile} isActive={activeLinkId === item.sys.id} />
					</TAGLink>}

				{!isMobile && <Menu id={groupMenuId} disableScrollLock={true} open={open} anchorEl={anchorEl} onClose={handleClose} sx={dropMenuStyles} transitionDuration={1}>
						{item.linksCollection?.items?.map((navItem, index) => navItem ? <GroupNavigationLink key={navItem.sys.id} navItem={navItem} index={index} isMobile={isMobile} /> : null)}
					</Menu>}
				{isMobile && <Collapse in={open} sx={collapseMenuItemStyles}>
						{item.linksCollection?.items?.map((navItem, index) => navItem ? <GroupNavigationLink key={navItem.sys.id} navItem={navItem} index={index} isMobile={isMobile} /> : null)}
					</Collapse>}
			</Box>;
  }
  if (itemIsNavigationLink(item)) {
    return <NavigationLink {...item} isInternal={item.isInternal || false} itemIndex={itemIndex} dataTestId={`link_page_nav_item_${item.sys.id}`} eventType="navigation"
    // Match the group header's larger font so all top-level mobile items look the same
    textVariant={isMobile ? 'bodyLargeSemiBold' : undefined} navLinkSx={{
      ...(isMobile ? {
        ...navMobileStyles,
        ...mobileNavLinkTypographyResetStyles
      } : getNavItemStyles(linkColor, fontWeight)),
      ...(isMobile ? {} : navLinkHeight),
      ...navLinkStyles
    }} isUtilityNavigation={isUtilityNavigation} linkColor={linkColor} showIconText={showIconText} iconSize={iconSize} />;
  }
  incorrectUsageWarning('NavigationBarItem', `Unsupported Content Type for HeaderNavigationLinksItem type passed to the NavigationBar. NavigationBarItem must be of type NavigationLink or NavigationGroup. (Page or FooterNavigationGroup support would be removed once all content are using NavigationLink and NavigationGroup).`);
  return null;
}